import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  PersonaTipoUtenteEnum,
  StatusRegistrazioneStatusEnum,
} from '../../../../api/generated/apc/api';
import { env } from '../../../../env';
import { ApiRequestStatus } from '../../../../model/api-request';
import { GlobalActions } from '../../../../model/global-actions';
import appRoutes from '../../../../routing/app-routes';
import {
  setUserCodeSetupAccountUnchanged,
  UserRegistrationState,
} from '../../../../store';
import {
  clearGetStatusRegistrazioneError,
  clearSetupAccountError,
  resetGetStatusRegistrazioneState,
  resetSetupAccountState,
  resetUserRegistrationState,
  setFiscalCodeSetupAccount,
  setUserCodeSetupAccount,
  setVatCodeFiscalCodeSetupAccount,
} from '../../../../store/features/user-access/user-registration/user-registration-slice';
import {
  getStatusRegistrazione,
  setupAccount,
} from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { messagesForSetupAccountError } from '../../../../store/features/user-access/user-registration/user-registration-utils';
import { useAppSelector } from '../../../redux';

export interface InsertDataPageLogicCallbacks {
  buttonDisabled: boolean;
  selectedOptionUserType: string;
  userCode: string;
  userBehalfOfCode: string;
  userCodeUnchanged: string;
  fiscalCode: string;
  vatCodeFiscalCode: string;
  setupAccountRequestStatus: ApiRequestStatus;
  getStatusRegistrazioneRequestStatus: ApiRequestStatus;
  fiduciaria: boolean;
  goBack: () => void;
  handleSubmit: () => void;
  updateUserCode: (value?: string) => void;
  updateFiscalCode: (value?: string) => void;
  updateVatCodeFiscalCode: (value?: string) => void;
}

export const useInsertDataPageLogic = (
  actions: GlobalActions
): InsertDataPageLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const {
    selectedOptionUserType,
    setupAccountRequestStatus,
    setupAccountError,
    userCode,
    userBehalfOfCode,
    userCodeUnchanged,
    fiscalCode,
    vatCodeFiscalCode,
    fiduciaria,
    getStatusRegistrazioneRequestStatus,
    getStatusRegistrazioneResult,
    getStatusRegistrazioneError,
  }: UserRegistrationState = useAppSelector(
    (state) => state.features.userRegistration
  );

  const [buttonDisabled, setButtonDisabled] = useState(
    (userCode?.length === 0 && fiscalCode?.length === 0) ||
      (userCode?.length === 0 && vatCodeFiscalCode?.length === 0)
  );

  const updateUserCode = useCallback(
    (value: string) => {
      dispatch(setUserCodeSetupAccountUnchanged(value));
      setButtonDisabled(false);
      dispatch(setUserCodeSetupAccount(value));
    },
    [dispatch]
  );
  const updateFiscalCode = useCallback(
    (value: string) => {
      setButtonDisabled(false);
      dispatch(setFiscalCodeSetupAccount(value));
    },
    [dispatch]
  );
  const updateVatCodeFiscalCode = useCallback(
    (value: string) => {
      setButtonDisabled(false);
      dispatch(setVatCodeFiscalCodeSetupAccount(value));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    dispatch(getStatusRegistrazione());
  }, [dispatch]);

  // Effect to handle the result of getStatusRegistrazione
  useEffect(() => {
    if (
      getStatusRegistrazioneRequestStatus === 'succeeded' &&
      !getStatusRegistrazioneError
    ) {
      dispatch(resetGetStatusRegistrazioneState());
      switch (getStatusRegistrazioneResult) {
        // If there is no registration, proceed to setup account
        case StatusRegistrazioneStatusEnum.NessunaRegistrazione:
        case StatusRegistrazioneStatusEnum.RegistrazioneInCorso:
          dispatch(setupAccount());
          break;
        // If the registration is active, navigate to the signature area
        case StatusRegistrazioneStatusEnum.BundleAttivo:
          navigate(appRoutes.SignatureAreaUserRegistration);
          break;
        // If the registration is in progress or completed, show an error toast
        case StatusRegistrazioneStatusEnum.RegistrazioneCompletata:
          showToast({
            type: 'error',
            title: formatMessage(
              messagesForSetupAccountError('UTENTE_GIA_REGISTRATO').title
            ),
            close: closeToast,
          });
          break;
      }
    }
  }, [
    getStatusRegistrazioneRequestStatus,
    getStatusRegistrazioneError,
    getStatusRegistrazioneResult,
  ]);

  const goBack = useCallback(() => {
    navigate(-1);
    dispatch(resetUserRegistrationState());
  }, [dispatch, navigate]);

  useEffect(() => {
    if (setupAccountRequestStatus === 'succeeded') {
      dispatch(resetSetupAccountState());
      if (selectedOptionUserType === PersonaTipoUtenteEnum.Pf) {
        navigate(appRoutes.WelcomeUserRegistration, { replace: false });
      } else {
        navigate(appRoutes.InsertFiscalCodeUserRegistration, {
          replace: false,
        });
      }
    }
  }, [dispatch, navigate, selectedOptionUserType, setupAccountRequestStatus]);

  useEffect(() => {
    if (getStatusRegistrazioneError) {
      const errorMessages = messagesForSetupAccountError(
        getStatusRegistrazioneError
      );
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        close: closeToast,
      });
      dispatch(clearGetStatusRegistrazioneError());
    }
  }, [
    getStatusRegistrazioneError,
    dispatch,
    formatMessage,
    showToast,
    closeToast,
  ]);

  useEffect(() => {
    if (setupAccountError) {
      const errorMessages = messagesForSetupAccountError(setupAccountError);
      const link =
        setupAccountError === 'UTENZA_NON_MIGRATA'
          ? env.oldMyAzimutUrl
          : undefined;

      if (link && env.appModule === 'web') {
        window.location.replace(
          link +
            '?page=REGISTRATION' +
            '&cCli=' +
            userCode +
            '&subject=' +
            selectedOptionUserType +
            (selectedOptionUserType === PersonaTipoUtenteEnum.Pf
              ? '&cFisc=' + fiscalCode
              : '&pIva=' + vatCodeFiscalCode)
        );
        return;
      }

      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title, {
          a: (...chunks) =>
            `<a href=${link} target="_self" rel="noopener noreferrer">${chunks}</a>`,
        }),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(clearSetupAccountError());
    }
  }, [
    closeToast,
    dispatch,
    fiscalCode,
    formatMessage,
    selectedOptionUserType,
    setupAccountError,
    showToast,
    userCode,
    vatCodeFiscalCode,
  ]);

  return {
    buttonDisabled,
    selectedOptionUserType,
    userCode,
    userBehalfOfCode,
    userCodeUnchanged,
    fiscalCode,
    fiduciaria,
    vatCodeFiscalCode,
    setupAccountRequestStatus,
    getStatusRegistrazioneRequestStatus,
    goBack,
    handleSubmit,
    updateUserCode,
    updateFiscalCode,
    updateVatCodeFiscalCode,
  };
};
